/**
 * Purpose: build the Generate-image dialog prompts from the live design.
 *
 * Input:  geometry format, camera, location, {@link BuildingDesign}.
 * Output: system prompt (massing + rules) and description (look / mood).
 * Consumers: Generate image dialog (map capture and report cover).
 */

import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Coordinate } from '@shared/models/geography.model';
import type { User } from '@shared/models/user.model';
import { isAdmin } from '@shared/helpers/user.helpers';
import {
  MapRenderRequest,
  type MapCameraState,
} from '@shared/models/design/map-render.model';
import {
  describeBuildingDesignForRender,
  formatBuildingDesignGeometryJson,
  formatStoreyLockForDesign,
} from '@shared/helpers/design/building-design-render-prompt.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { PROMPT_STRINGS } from './design-ai-prompts.constants';

type RenderSourceType = MapRenderRequest['sourceType'];
type ViewMode = MapRenderRequest['mode'];

type BuildClientPromptInput = {
  sourceType: RenderSourceType;
  mode: ViewMode;
  camera: MapCameraState;
  location: Coordinate | undefined;
  buildingDesign: BuildingDesign;
};

const INPUT_INTERPRETATION: Record<RenderSourceType, string> = {
  'building-json': PROMPT_STRINGS.INPUT_JSON_ONLY,
  screenshot: PROMPT_STRINGS.INPUT_SCREENSHOT_ONLY,
  'screenshot+building-json': PROMPT_STRINGS.INPUT_COMBINED,
};

/**
 * Default system prompt: how to read JSON/screenshot plus the live massing.
 * Includes {@link PROMPT_STRINGS.USER_PROMPT_MACRO} and, when JSON is inlined,
 * {@link PROMPT_STRINGS.JSON_MACRO}. Admins may edit it; safety rules stay
 * server-side.
 */
export const buildClientControlledSystemPrompt = ({
  sourceType,
  mode,
  camera,
  location,
  buildingDesign,
}: BuildClientPromptInput): string => {
  const loc = guards.is.defined(location)
    ? `lat ${location.lat.toFixed(5)}, lng ${location.lng.toFixed(5)}`
    : 'Swedish site (lat/lng unknown)';

  const inputInterpretation = INPUT_INTERPRETATION[sourceType].replace(
    '{mode}',
    mode,
  );
  const storeyLock = formatStoreyLockForDesign(buildingDesign);

  return [
    PROMPT_STRINGS.TASK,
    '',
    storeyLock,
    '',
    PROMPT_STRINGS.SITE_AND_ROOF,
    '',
    ...sourceTypePromptSections(sourceType),
    PROMPT_STRINGS.GEOMETRY_RULES,
    '',
    describeBuildingDesignForRender(buildingDesign),
    '',
    ...sourceTypeJsonMacro(sourceType),
    inputInterpretation,
    '',
    `Camera: zoom ${camera.zoom.toFixed(2)}, pitch ${camera.pitch.toFixed(1)}°, bearing ${camera.bearing.toFixed(1)}°.`,
    `Location: ${loc}.`,
    PROMPT_STRINGS.SWEDISH_CONTEXT,
    PROMPT_STRINGS.STYLE_SCOPE,
    ...sourceTypeFidelityCheck(sourceType),
    '',
    PROMPT_STRINGS.USER_PROMPT_MACRO,
  ].join('\n');
};

/**
 * Prompt sections that depend on whether a screenshot, JSON, or both are attached.
 */
const sourceTypePromptSections = (sourceType: RenderSourceType): string[] => {
  switch (sourceType) {
    case 'screenshot':
      return [PROMPT_STRINGS.SCREENSHOT_TRACE, ''];
    case 'building-json':
      return [PROMPT_STRINGS.JSON_CONSTRUCTION, ''];
    case 'screenshot+building-json':
      return [
        PROMPT_STRINGS.SCREENSHOT_TRACE,
        '',
        PROMPT_STRINGS.JSON_CHECKLIST,
        '',
      ];
    default:
      return assertUnreachable(sourceType);
  }
};

/**
 * Placeholder for inlining geometry JSON in the system prompt (not a separate file).
 */
const sourceTypeJsonMacro = (sourceType: RenderSourceType): string[] => {
  switch (sourceType) {
    case 'screenshot':
      return [];
    case 'building-json':
    case 'screenshot+building-json':
      return [PROMPT_STRINGS.JSON_MACRO, ''];
    default:
      return assertUnreachable(sourceType);
  }
};

/**
 * Closing reminder: trace the screenshot, or keep JSON prisms when there is none.
 */
const sourceTypeFidelityCheck = (sourceType: RenderSourceType): string[] => {
  switch (sourceType) {
    case 'screenshot':
    case 'screenshot+building-json':
      return [PROMPT_STRINGS.TRACE_FIDELITY_CHECK];
    case 'building-json':
      return [PROMPT_STRINGS.JSON_FIDELITY_CHECK];
    default:
      return assertUnreachable(sourceType);
  }
};

/**
 * Default Description field: look and mood only.
 * Geometry, camera, and massing instructions live in the system prompt.
 */
export const buildDefaultUserPrompt = (): string =>
  PROMPT_STRINGS.DESCRIPTION_DEFAULT;

/**
 * Expand optional {@link PROMPT_STRINGS.USER_PROMPT_MACRO} and
 * {@link PROMPT_STRINGS.JSON_MACRO} in the system prompt. Omitted macros are
 * not filled in; the system prompt is otherwise the full text sent.
 */
export const mergeClientPrompt = (input: {
  systemPrompt: string;
  userPrompt: string;
  buildingDesign?: BuildingDesign;
}): string => {
  const userPrompt = input.userPrompt.trim();
  const hasJsonMacro = input.systemPrompt.includes(PROMPT_STRINGS.JSON_MACRO);
  const geometryJson =
    hasJsonMacro && input.buildingDesign
      ? formatBuildingDesignGeometryJson(input.buildingDesign)
      : '';
  // String split/join so `$` in the user prompt is not treated as a replace pattern.
  return input.systemPrompt
    .split(PROMPT_STRINGS.USER_PROMPT_MACRO)
    .join(userPrompt)
    .split(PROMPT_STRINGS.JSON_MACRO)
    .join(geometryJson)
    .trim();
};

/**
 * DevTools dumps of the map screenshot and prompt — admins and local Vite only.
 */
export const shouldLogMapRenderDebug = (user?: User): boolean =>
  import.meta.env.DEV || isAdmin(user);

/**
 * Print the expanded prompt that will be sent to the render endpoint.
 */
export const logRenderPromptToConsole = (prompt: string): void => {
  console.info('Map render prompt sent to AI:\n' + prompt);
};
